import React, { useContext } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  Lock,
  Receipt,
  Sparkles,
  Crown,
  Church,
  Users2,
  Calendar,
  Repeat,
  Building2,
  Download,
  Eye
} from 'lucide-react';

const PRO_PRICE = 149;

const plans = [
  {
    key: 'pro',
    name: 'Pro',
    price: '$149/mo',
    description: 'For individual creators building consistently.',
    icon: Crown,
    accent: 'pink',
    state: 'active',
    features: [
      'Create + Analyze full access',
      'Autopilot Assisted mode',
      'Connect — unified inboxes',
      'Standard support'
    ]
  },
  {
    key: 'church_pro',
    name: 'Church Pro',
    price: 'Contact for pricing',
    description: 'For churches & ministries running multi-creator content.',
    icon: Church,
    accent: 'amber',
    state: 'available',
    features: [
      'Everything in Pro',
      'Church Partner integration',
      'Multi-creator coordination',
      'Faith-community tooling'
    ]
  },
  {
    key: 'agency_plus',
    name: 'Agency+',
    price: 'Enterprise',
    description: 'Agencies, networks, and managed creator partnerships.',
    icon: Users2,
    accent: 'violet',
    state: 'locked',
    features: [
      'Everything in Church Pro',
      'Agency dashboards & roles',
      'Multi-brand support',
      'Dedicated success partner'
    ]
  }
];

const invoices = [
  { id: 'INV-004', date: 'Apr 15, 2026', amount: '$149.00', status: 'Paid' },
  { id: 'INV-003', date: 'Mar 15, 2026', amount: '$149.00', status: 'Paid' },
  { id: 'INV-002', date: 'Feb 15, 2026', amount: '$149.00', status: 'Paid' },
  { id: 'INV-001', date: 'Jan 15, 2026', amount: '$149.00', status: 'Paid' }
];

const StatusBadge = ({ state }) => {
  if (state === 'active') {
    return (
      <Badge variant="outline" className="border-green-500/30 text-green-300 bg-green-500/10 text-[10px] uppercase tracking-wider">
        Active
      </Badge>
    );
  }
  if (state === 'available') {
    return (
      <Badge variant="outline" className="border-white/15 text-[#C7CED9] bg-white/[0.04] text-[10px] uppercase tracking-wider">
        Available
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-violet-500/30 text-violet-300 bg-violet-500/10 text-[10px] uppercase tracking-wider">
      <Lock className="h-2.5 w-2.5 mr-1" />
      Locked
    </Badge>
  );
};

const PlanCard = ({ plan }) => {
  const Icon = plan.icon;
  const isActive = plan.state === 'active';
  const isLocked = plan.state === 'locked';
  return (
    <Card
      className={`glass border ${
        isActive
          ? 'border-pink-500/40 ring-1 ring-pink-500/20'
          : isLocked
          ? 'border-violet-500/25'
          : 'border-white/5'
      } relative overflow-hidden`}
      data-testid={`plan-card-${plan.key}`}
    >
      {isLocked && (
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-violet-500/[0.06] via-transparent to-transparent" />
      )}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-lg bg-${plan.accent}-500/10 border border-white/5 flex items-center justify-center`}>
              <Icon className={`h-4 w-4 text-${plan.accent}-400`} />
            </div>
            <div>
              <CardTitle className="text-base text-white">{plan.name}</CardTitle>
              <p className="text-xs text-[#8A93A6]">{plan.price}</p>
            </div>
          </div>
          <StatusBadge state={plan.state} />
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-[#8A93A6] leading-relaxed">{plan.description}</p>
        <ul className="space-y-1.5">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-xs text-[#C7CED9]">
              <CheckCircle2 className={`h-3.5 w-3.5 text-${plan.accent}-400 mt-0.5 shrink-0`} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const DetailRow = ({ icon: Icon, label, value, accent = 'pink' }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-white/[0.04] last:border-0">
    <div className="flex items-center gap-2.5">
      <div className={`w-7 h-7 rounded-md bg-${accent}-500/10 border border-white/5 flex items-center justify-center`}>
        <Icon className={`h-3.5 w-3.5 text-${accent}-400`} />
      </div>
      <span className="text-xs text-[#8A93A6]">{label}</span>
    </div>
    <span className="text-sm font-medium text-white">{value}</span>
  </div>
);

const Billing = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-testid="account-billing-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Billing</h1>
        <p className="text-[#8A93A6]">Subscription, payment methods, and billing history.</p>
      </div>

      {/* 1. Subscription Status */}
      <Card className="glass mb-6" data-testid="subscription-status-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 border border-green-500/15 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <CardTitle className="text-base text-white flex items-center gap-2">
                  Subscription Status
                  {isAdmin && (
                    <Badge variant="outline" className="border-pink-500/30 text-pink-300 bg-pink-500/10 text-[10px] uppercase tracking-wider">
                      Admin Preview
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-[#8A93A6]">
                  Billing and subscription management are handled securely through Consistency.
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="border-green-500/30 text-green-300 bg-green-500/10 text-[10px] uppercase tracking-wider hidden sm:inline-flex">
              Active
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* 2. Current Plan */}
      <Card className="glass mb-6" data-testid="current-plan-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base text-white">Current Plan</CardTitle>
              <CardDescription className="text-[#8A93A6]">Your active subscription</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-pink-500/30 text-pink-300 bg-pink-500/10 text-[10px] uppercase tracking-wider">
                <Sparkles className="h-2.5 w-2.5 mr-1" />
                Lifetime Early Access
              </Badge>
              <Badge variant="outline" className="border-white/15 text-[#C7CED9] bg-white/[0.03] text-[10px] uppercase tracking-wider">
                Early Access Pricing
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="text-4xl font-bold text-white">Pro</h2>
            <span className="text-2xl font-semibold text-white">${PRO_PRICE}</span>
            <span className="text-sm text-[#8A93A6]">/month</span>
          </div>
          <p className="text-xs text-[#8A93A6]">
            You are on Early Access Pricing. Standard Pricing applies to new subscribers after Public Launch.
          </p>
        </CardContent>
      </Card>

      {/* 3. Subscription Details */}
      <Card className="glass mb-6" data-testid="subscription-details-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white">Subscription Details</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow icon={Crown} label="Current Plan" value="Pro" accent="pink" />
          <DetailRow icon={CheckCircle2} label="Billing Status" value="Active" accent="green" />
          <DetailRow icon={Calendar} label="Renewal Date" value="May 15, 2026" accent="blue" />
          <DetailRow icon={Repeat} label="Billing Cycle" value="Monthly" accent="cyan" />
          <DetailRow icon={Building2} label="Subscription Source" value="Pre-Sales" accent="amber" />
        </CardContent>
      </Card>

      {/* 4. Available Plans — Admin Preview */}
      {isAdmin && (
        <div className="mb-6" data-testid="available-plans-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-white">Available Plans</h3>
              <p className="text-xs text-[#8A93A6]">Plan architecture preview — visual verification only.</p>
            </div>
            <Badge variant="outline" className="border-pink-500/30 text-pink-300 bg-pink-500/10 text-[10px] uppercase tracking-wider">
              Admin Preview
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <PlanCard key={p.key} plan={p} />
            ))}
          </div>
        </div>
      )}

      {/* 5. Payment Methods */}
      <Card className="glass mb-6" data-testid="payment-methods-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-blue-400" />
            Payment Methods
          </CardTitle>
          <CardDescription className="text-[#8A93A6]">
            Payment methods and subscription updates are managed securely.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-7 rounded bg-gradient-to-br from-blue-500/30 to-blue-700/30 border border-white/5 flex items-center justify-center">
                <span className="text-[10px] font-bold text-white tracking-wider">VISA</span>
              </div>
              <div>
                <p className="text-sm text-white">Visa •••• 4242</p>
                <p className="text-[11px] text-[#8A93A6]">Expires 12/28</p>
              </div>
            </div>
            <Badge variant="outline" className="border-white/15 text-[#C7CED9] bg-white/[0.03] text-[10px] uppercase tracking-wider">
              Primary
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* 6. Billing History */}
      <Card className="glass mb-6" data-testid="billing-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Receipt className="h-4 w-4 text-amber-400" />
            Billing History
          </CardTitle>
          <CardDescription className="text-[#8A93A6]">Past invoices and receipts.</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#8A93A6] border-b border-white/5">
                  <th className="text-left font-medium px-6 py-2">Invoice #</th>
                  <th className="text-left font-medium px-3 py-2">Date</th>
                  <th className="text-left font-medium px-3 py-2">Amount</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="px-6 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/[0.03] last:border-0" data-testid={`invoice-${inv.id}`}>
                    <td className="px-6 py-3 font-medium text-white tabular-nums">{inv.id}</td>
                    <td className="px-3 py-3 text-[#C7CED9]">{inv.date}</td>
                    <td className="px-3 py-3 text-[#C7CED9] tabular-nums">{inv.amount}</td>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className="border-green-500/30 text-green-300 bg-green-500/10 text-[10px] uppercase tracking-wider">
                        {inv.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Button variant="ghost" size="sm" className="text-[#8A93A6] hover:text-white h-7 px-2">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 7. Feature Access Preview — Admin only */}
      {isAdmin && (
        <Card className="glass" data-testid="feature-access-preview-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Eye className="h-4 w-4 text-pink-400" />
                  Feature Access Preview
                </CardTitle>
                <CardDescription className="text-[#8A93A6]">
                  Visual verification of subscription gating.
                </CardDescription>
              </div>
              <Badge variant="outline" className="border-pink-500/30 text-pink-300 bg-pink-500/10 text-[10px] uppercase tracking-wider">
                Admin Preview
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#8A93A6] mb-2">Current Access</p>
              <div className="flex items-center gap-2.5 rounded-lg border border-green-500/20 bg-green-500/[0.05] px-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span className="text-sm text-white">Pro Features</span>
                <Badge variant="outline" className="ml-auto border-green-500/30 text-green-300 bg-green-500/10 text-[10px] uppercase tracking-wider">
                  Enabled
                </Badge>
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#8A93A6] mb-2">Upgradeable Access</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2.5 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5">
                  <div className="h-4 w-4 rounded-full border border-amber-400/60" />
                  <span className="text-sm text-white">Church Pro</span>
                  <Badge variant="outline" className="ml-auto border-amber-500/30 text-amber-300 bg-amber-500/10 text-[10px] uppercase tracking-wider">
                    Available
                  </Badge>
                </div>
                <div className="flex items-center gap-2.5 rounded-lg border border-violet-500/15 bg-violet-500/[0.04] px-3 py-2.5">
                  <Lock className="h-4 w-4 text-violet-400" />
                  <span className="text-sm text-white">Agency+</span>
                  <Badge variant="outline" className="ml-auto border-violet-500/30 text-violet-300 bg-violet-500/10 text-[10px] uppercase tracking-wider">
                    Gated
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Billing;
